import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import { LoginUserEmailPasswordApi } from '../../Api';
import { notify } from '../../components/Notify';
import { UEN_TOKEN_KEY, UEN_USER_KEY } from '../../utils';
import { validateEmail } from '../../validators';
import loginImg from '../../assets/loginImg.jpg';
import styles from './LoginUser.module.css';

export default function LoginUser() {
    const navigate = useNavigate();
    const location = useLocation();
    // Vindo do /registrar quando email/CPF já estava cadastrado.
    const prefilledEmail = location.state?.prefilledEmail || '';

    const [email, setEmail] = useState(prefilledEmail);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    function validate() {
        const next = {};
        const e = validateEmail(email);
        if (!e.valid) next.email = e.error;
        if (!password) next.password = 'Informe a senha.';
        else if (password.length < 6) next.password = 'Senha muito curta.';
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    const handleSignIn = async (e) => {
        e.preventDefault();
        if (loading) return;
        if (!validate()) return;

        setLoading(true);
        try {
            const res = await LoginUserEmailPasswordApi({ email: email.trim(), password });
            if (res.data.token) {
                localStorage.setItem(UEN_TOKEN_KEY, res.data.token);
                if (res.data.user) {
                    localStorage.setItem(UEN_USER_KEY, JSON.stringify(res.data.user));
                }
                // Sinaliza "acabou de logar" pra o SidebarLayout decidir se mostra
                // o modal de confirmação de email (só aparece se ainda não confirmou).
                sessionStorage.setItem('uen_email_prompt_pending', '1');
                notify('Bem-vindo ao sistema UEN!', 'success');
                navigate('/dashboard');
            } else {
                notify('Resposta inválida do servidor.', 'error');
            }
        } catch (err) {
            console.error('Erro no login:', err);
            notify(err.response?.data?.error || 'Erro ao conectar com o servidor.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            {/* ---------- Form section ---------- */}
            <section className={styles.formSection}>
                <div className={styles.formContainer}>
                    <div className={styles.brand}>
                        <div className={styles.brandLogo}>U</div>
                        <div className={styles.brandText}>
                            <span className={styles.brandName}>UEN</span>
                            <span className={styles.brandTag}>Estudantil</span>
                        </div>
                    </div>

                    <h1 className={styles.title}>Bem-vindo de volta</h1>
                    <p className={styles.subtitle}>Acesse sua conta para ver sua carteirinha.</p>

                    <form onSubmit={handleSignIn} noValidate>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="email">E-mail</label>
                            <div className={styles.inputWrapper}>
                                <span className={styles.inputIcon}><FiMail size={18} /></span>
                                <input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    placeholder="seu@email.com"
                                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (errors.email) setErrors(prev => ({ ...prev, email: undefined }));
                                    }}
                                    onBlur={() => {
                                        if (!email) return;
                                        const r = validateEmail(email);
                                        setErrors(prev => ({ ...prev, email: r.valid ? undefined : r.error }));
                                    }}
                                />
                            </div>
                            {errors.email && (
                                <div className={styles.fieldError}>
                                    <FiAlertCircle size={14} /> {errors.email}
                                </div>
                            )}
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="password">Senha</label>
                            <div className={styles.inputWrapper}>
                                <span className={styles.inputIcon}><FiLock size={18} /></span>
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    placeholder="••••••••"
                                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                                    }}
                                />
                                <button
                                    type="button"
                                    className={styles.eyeButton}
                                    onClick={() => setShowPassword(s => !s)}
                                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>
                            {errors.password && (
                                <div className={styles.fieldError}>
                                    <FiAlertCircle size={14} /> {errors.password}
                                </div>
                            )}
                        </div>

                        <div className={styles.forgotRow}>
                            <Link to="/recuperar-acesso" className={styles.forgotLink}>
                                Esqueci minha senha ou Recuperar Conta
                            </Link>
                        </div>

                        <button type="submit" className={styles.submitButton} disabled={loading}>
                            {loading ? (
                                <>
                                    <span className={styles.spinner} />
                                    Entrando...
                                </>
                            ) : (
                                <>
                                    Entrar
                                    <FiArrowRight size={18} />
                                </>
                            )}
                        </button>
                    </form>

                    <div className={styles.signupRow}>
                        Não tem uma conta?
                        <Link to="/registrar" className={styles.signupLink}>Cadastre-se</Link>
                    </div>
                </div>
            </section>

            {/* ---------- Illustration section (imagem) ---------- */}
            <aside className={styles.illustration} aria-hidden="true">
                <img
                    src={loginImg}
                    alt=""
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            </aside>
        </div>
    );
}
